import { Component, EventEmitter, Input, Output } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MatIcon } from '@angular/material/icon'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatTooltip } from '@angular/material/tooltip'
import { MatDialog } from '@angular/material/dialog'
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop'
import { ParadaItinerario } from '../../../core/models/parada-itinerario.model'
import {
  ParadaFormData,
  ParadaItinerarioFormComponent,
} from '../parada-itinerario-form/parada-itinerario-form.component'

@Component({
  selector: 'parada-itinerario-editor',
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatButton,
    MatIconButton,
    MatTooltip,
    CdkDropList,
    CdkDrag,
  ],
  templateUrl: './parada-itinerario-editor.component.html',
  styleUrl: './parada-itinerario-editor.component.scss',
})
export class ParadaItinerarioEditorComponent {
  @Input() paradas: ParadaItinerario[] = []
  @Input() readonly = false
  @Input() showPromoteButton = false
  @Input() title = 'Paradas do itinerário'
  @Input() location: ParadaFormData['location'] = {}

  @Output() paradasChange = new EventEmitter<ParadaItinerario[]>()
  @Output() promoteRequested = new EventEmitter<void>()

  constructor(private dialog: MatDialog) {}

  trackByOrdem = (_: number, p: ParadaItinerario) => p.ordem

  addParada(): void {
    const dialogRef = this.dialog.open(ParadaItinerarioFormComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '900px',
      minWidth: '900px',
      data: {
        edit: false,
        location: this.location,
      } as ParadaFormData,
    })

    dialogRef.afterClosed().subscribe((result: ParadaItinerario | null) => {
      if (!result) return
      const nova = { ...result, ordem: this.paradas.length + 1 }
      const next = [...this.paradas, nova]
      this.emit(next)
    })
  }

  editParada(index: number): void {
    const current = this.paradas[index]
    const dialogRef = this.dialog.open(ParadaItinerarioFormComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '900px',
      minWidth: '900px',
      data: {
        edit: true,
        parada: current,
        location: this.location,
      } as ParadaFormData,
    })

    dialogRef.afterClosed().subscribe((result: ParadaItinerario | null) => {
      if (!result) return
      const next = [...this.paradas]
      next[index] = { ...result, ordem: current.ordem }
      this.emit(next)
    })
  }

  removeParada(index: number): void {
    const next = this.paradas.filter((_, i) => i !== index)
    this.emit(this.renumber(next))
  }

  drop(event: CdkDragDrop<ParadaItinerario[]>): void {
    if (this.readonly) return
    const next = [...this.paradas]
    moveItemInArray(next, event.previousIndex, event.currentIndex)
    this.emit(this.renumber(next))
  }

  promoveItinerario(): void {
    this.promoteRequested.emit()
  }

  private renumber(list: ParadaItinerario[]): ParadaItinerario[] {
    return list.map((p, i) => ({ ...p, ordem: i + 1 }))
  }

  private emit(list: ParadaItinerario[]): void {
    this.paradas = list
    this.paradasChange.emit(list)
  }

  hasParadaIntermediaria(): boolean {
    return this.paradas.some((p) => (p.trecho ?? 'P') === 'P')
  }

  paradaLine(p: ParadaItinerario): string {
    const parts = [
      p.localParada,
      [p.logradouro, p.numero].filter(Boolean).join(', '),
      [p.cidade, p.estado].filter(Boolean).join(' - '),
    ].filter(Boolean)
    return parts.join(' • ')
  }
}
